import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { RealtimeChannel, Session } from "@supabase/supabase-js"
import debounce from "lodash.debounce"
import { jwtDecode } from "jwt-decode"

import { ReactComponent as MinimapIcon } from "./assets/icons/minimap.svg"
import { ReactComponent as SelectionLockedIcon } from "./assets/icons/selection-locked-icon.svg"
import { ReactComponent as SelectionUnlockedIcon } from "./assets/icons/selection-unlocked-icon.svg"

import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Node,
  Connection,
  ReactFlowInstance,
  EdgeProps,
  Edge,
  Viewport,
  NodePositionChange,
  NodeChange,
  MiniMap,
  getNodesBounds,
} from "reactflow"
import { ApiCell, PublishedApi, supabase } from "./database/SupabaseConnector"

import "./App.css"
import "reactflow/dist/style.css"
import {
  AppContext,
  CellWithContextMenuInfoType,
  MultiplayerSelectionState,
  MultiselectedCells,
} from "./AppContext"
import {
  SheetField,
  UICell,
  UIColumn,
  UIEdge,
  UIFlowChartChoiceBlock,
  UILogic,
  UILogicType,
  UISheet,
  UISheetType,
  UserProfile,
} from "./types/UITypes"
import { EdgeRenderer } from "./components/EdgeRenderer"
import { EdgePreview } from "./components/EdgePreview"
import { RemovesCellsOptions, removeCells } from "./utils/removeCells"
import {
  CustomerLLMUsage,
  CustomerSubscription,
  DatabaseCanvas,
  DatabaseCustomer,
  DatabaseLogic,
  SubscriptionTier,
} from "./types/SupabaseTypesHelper"
import { getApiParams } from "./utils/apiUtils"
import { useSupabaseConnector } from "./hooks/useSupabaseConnector"
import "react-json-view-lite/dist/index.css"
import {
  CANVAS_SIZE,
  CELL_WIDTH,
  comparisonFormulaRegExp,
} from "./utils/constants"
import LogicRenderer from "./components/logic-renderer/LogicRenderer"
import SheetRenderer from "./components/sheet-renderer/SheetRenderer"
import MatrixRenderer from "./components/sheet-renderer/MatrixRenderer"
import {
  addCells,
  addColumn,
  addRow,
  BACKGROUND_DOTS_GAP,
  getClusters,
  getDraggedNodePosition,
  pasteCells,
} from "./utils/2dsheet"
import { buildCellsDependencyTree } from "./utils/buildCellDependencyTree"
import { getFirebaseTemplate } from "./utils/getFirebaseApiFunction"
import { SheetValidationType } from "./utils/validation"
import { getFullURL } from "./utils/common"
import { AutogenModal } from "./components/AutogenModal"
import { UpgradeToProModal } from "./components/UpgradeToProModal"
import { PublishApiModal } from "./components/PublishApiModal"
import { ManageSubscriptionModal } from "./components/ManageSubscriptionModal"
import { FlowChartChoiceBlock } from "./components/flowchart-choice-block/FlowChartChoiceBlock"
import { addSheet } from "./utils/addSheet"
import { addConditionBlock } from "./utils/addConditionBlock"
import {
  confirmMatrixLabel,
  updateColumn,
  updateRowLabel,
} from "./utils/labelUtils"
import { autogenerateCanvas } from "./utils/autogenerateCanvas"
import { updateViewport } from "./utils/updateViewport"
import { resizeFlowchartCondition } from "./utils/resizeFlowchartCondition"
import { addEdgeConnection, removeEdgeConnection } from "./utils/edgeUtils"
import { ToolBar } from "./components/ToolBar"
import { AutogenResultsModal } from "./components/AutogenResultsModal"
import { UserModal } from "./components/UserModal"
import { MatrixLabelEditorModal } from "./components/MatrixLabelEditorModal"
import { getCenteredViewport } from "./components/logic-renderer/utils"
import { uuidv7 } from "uuidv7"
import { getValidationForApi } from "./utils/getValidationForApi"
import { CELL_CANNOT_BE_EVALUATED } from "./utils/validationConstants"
import { MultiplayerUserMarker } from "./components/MultiplayerUserMarker"
import { ProfileModal } from "./components/ProfileModal"
import { MultiplayerUserIndicator } from "./components/MultiplayerUserIndicator"
import { ApiTestModal } from "./components/ApiTestModal"
import ErrorBoundary from "./components/ErrorBoundary"
import { MultiselectToolbar } from "./components/MultiselectToolbar"

export type MultiplayerState = {
  mouseX: number
  mouseY: number
  selectedNodes?: string[]
  selectedEdges?: string[]
  selectedCell?: string
  timestamp: number
  userId: string
  firstName: string
  lastName: string
  userPicture?: string
}

const nodeTypes = {
  sheet: SheetRenderer,
  matrix: MatrixRenderer,
  logic: LogicRenderer,
  condition: FlowChartChoiceBlock,
}

const edgeTypes = {
  custom: EdgeRenderer,
}

const App = () => {
  const [session, setSession] = useState<Session | null | undefined>()
  const [nodes, setNodes] = useNodesState<
    UISheet | UILogic | UIFlowChartChoiceBlock
  >([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])
  const [selectedEdges, setSelectedEdges] = useState<string[]>([])
  const [isInitialized, setInitialized] = useState(false)
  const [sheetFields, setSheetFields] = useState<SheetField[]>([])
  const [api, setApi] = useState<PublishedApi[]>()
  const [cellsDependencyTree, setCellsDependencyTree] = useState<
    Record<string, { logics: string[]; cells: string[]; cellsData: ApiCell[] }>
  >({})
  const [isApiPreviewVisible, setIsApiPreviewVisible] = useState(false)
  const [connectionStart, setConnectionStart] =
    useState<Pick<Connection, "source" | "sourceHandle">>()
  const [canvas, setCanvas] = useState<DatabaseCanvas>()
  const [isUserModalOpen, setUserModalOpen] = useState(false)
  const [isMatrixLabelEditorOpen, setMatrixLabelEditorOpen] = useState(false)
  const [userType, setUserType] = useState<"Dev" | "PMBiz" | "Superuser">("Dev")
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>([])
  const [isEditingLabel, setIsEditingLabel] = useState(false)
  const [isDraggingColumnOrRow, setIsDraggingColumnOrRow] = useState(false)
  const [isPendingApiResponse, setPendingApiResponse] = useState(false)
  const [isPendingNodesUpdate, setPendingNodesUpdate] = useState(false)
  const [isSheetsUIVisible, setIsSheetsUIVisible] = useState(false)
  const reactFlowRef = useRef<ReactFlowInstance | null>(null)
  const matrixLabelInputRef = useRef<HTMLInputElement | null>(null)
  const isDraggingNode = useRef<boolean>(false)
  const refreshApiPreviewTimeout = useRef<NodeJS.Timeout>()
  const syncMultiplayerStateTimeout = useRef<NodeJS.Timeout>()
  const syncupLLMUSageTimeout = useRef<NodeJS.Timeout>()
  const syncupLLMUSageInterval = useRef<NodeJS.Timeout>()
  const [backendError, setBackendError] = useState<string | null>(null)
  const supabaseConnector = useSupabaseConnector(canvas?.id!)

  supabaseConnector.setBackendError = setBackendError

  const [isSelectionLocked, setSelectionLocked] = useState(false)

  const [clusters, setClusters] = useState<
    Record<string, Node<UISheet | UILogic>[]>
  >({})

  const multiplayerChannel = useRef<RealtimeChannel>()

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const pendingChanges: Record<string, NodeChange> = {}
      let hasPendingChanges = false
      // Get nodes from the reactFlowRef instance, in order not to add nodes to the callback function dependencies.
      // Since it will change the function on everry node change.
      const nodes = reactFlowRef.current?.getNodes() || []
      const nodesById = nodes.reduce((acc, node) => {
        acc[node.id] = node
        return acc
      }, {} as Record<string, Node>)

      changes.forEach((change) => {
        if (change.type === "position" && change.position) {
          const cluster = isSelectionLocked && clusters[change.id]

          if (cluster) {
            const node = nodes.find((node) => node.id === change.id)
            const positionDelta = {
              x: change.position.x - node?.position.x!,
              y: change.position.y - node?.position.y!,
            }

            cluster.forEach(({ id }) => {
              const node = nodesById[id]
              pendingChanges[node.id] = {
                ...change,
                position: {
                  x: node.position.x + positionDelta.x,
                  y: node.position.y + positionDelta.y,
                },
              }
            })
          } else {
            pendingChanges[change.id] = change
          }

          hasPendingChanges = true
        }

        if (change.type === "select") {
          pendingChanges[change.id] = change

          hasPendingChanges = true
        }
      })

      if (hasPendingChanges) {
        setNodes((nodes) => {
          const updatedNodes = nodes.map((node) => {
            const change: NodeChange = pendingChanges[node.id]

            if (change) {
              if (
                change.type === "position" &&
                (change as NodePositionChange).position
              ) {
                return {
                  ...node,
                  position: change.position!,
                }
              }

              if (change.type === "select") {
                return {
                  ...node,
                  selected: change.selected,
                }
              }

              return node
            }

            return node
          })

          return updatedNodes
        })
      }
    },
    [clusters, isSelectionLocked]
  )

  const handleInit = (reactFlowInstance: ReactFlowInstance) => {
    reactFlowRef.current = reactFlowInstance
  }

  const handleConnect = useCallback(
    async (connection: Connection) => {
      await addEdgeConnection(
        connection,
        nodes,
        edges,
        setEdges,
        setNodes,
        reactFlowRef,
        supabaseConnector
      )
    },
    [nodes, edges, session]
  )

  const [defaultViewport, setDefaultViewport] = useState<Viewport>(
    getCenteredViewport()
  )

  const debouncedUpdateViewport = (viewport: Viewport) => {
    if (canvas?.id && session?.user?.id) {
      updateViewport(viewport, canvas?.id!, session?.user?.id!)
    }
  }

  const [viewportData, setViewportData] = useState<Viewport>(defaultViewport)

  const handleViewportChange = (viewport: Viewport) => {
    debouncedUpdateViewport(viewport)
    setViewportData(viewport)
  }

  const multiselectControls = useMemo<{ x: number; y: number } | null>(() => {
    const isMultiselectMode = nodes.filter((node) => node.selected).length > 1

    if (!isMultiselectMode) {
      return null
    }

    const selectionBox = getNodesBounds(nodes.filter((node) => node.selected))
    const x = selectionBox.x + selectionBox.width
    const y = selectionBox.y

    return reactFlowRef.current!.flowToScreenPosition({
      x,
      y,
    })
  }, [nodes, viewportData])

  const startPollingLLMUsage = (session: any) => {
    clearInterval(syncupLLMUSageInterval.current)

    syncupLLMUSageInterval.current = setTimeout(async () => {
      const customerId = jwtDecode<{ customer_id: string }>(
        session?.access_token!
      )?.customer_id!

      const llmUsage = await supabaseConnector.getLLMUsage(customerId)

      llmUsage && setLLMUsage(llmUsage)
    }, 60000)
  }

  const [customer, setCustomer] = useState<DatabaseCustomer>()
  const [customerTeam, setCustomerTeam] = useState<string>()

  useEffect(() => {
    supabase.auth.getSession().then((response) => {
      const {
        data: { session },
      } = response
      setSession(session)

      const decodedToken = jwtDecode<{ customer_name: string }>(
        session?.access_token!
      )

      setCustomerTeam(decodedToken?.customer_name)

      // if (session) {
      //   startPollingLLMUsage(session)
      //   if (session?.access_token) {
      //     fetchData(session)
      //   }
      // }
    })

    const authChangeEvent = supabase.auth.onAuthStateChange(
      debounce((_event, session) => {
        setSession(session)

        if (session) {
          const decodedToken = jwtDecode<{ customer_name: string }>(
            session?.access_token!
          )

          setCustomerTeam(decodedToken?.customer_name)
          startPollingLLMUsage(session)
          if (session?.access_token) {
            fetchData(session)
          }
        }

        if (!session) {
          setInitialized(false)
          setUserModalOpen(false)
          setNodes([])
          setEdges([])
          setApi([])
        }
      }, 100)
    )

    return () => {
      authChangeEvent.data.subscription.unsubscribe()
      clearTimeout(syncupLLMUSageTimeout.current)
      clearInterval(syncupLLMUSageInterval.current)
      clearTimeout(refreshApiPreviewTimeout.current)
      clearTimeout(syncMultiplayerStateTimeout.current)
    }
  }, [])

  // useEffect(() => {
  //   if (session?.user && !isInitialized) {
  //     fetchData()
  //   }
  // }, [session, isInitialized])

  const isSyncDataNeeded = useRef(true)

  const syncData = async () => {
    const response = await supabaseConnector.fetchData()

    if (!response) {
      return
    }

    const { sheets, logics, edges } = response

    if (!sheets) {
      return
    }

    isSyncDataNeeded.current = false

    setNodes([...sheets, ...logics])
    setEdges(edges)
  }

  const [customerSubscription, setCustomerSubscription] =
    useState<CustomerSubscription>()
  const [llmUsage, setLLMUsage] = useState<CustomerLLMUsage>()
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>()

  const fetchData = async (session: Session) => {
    const canvases = await supabaseConnector.getCanvases()
    let canvas: DatabaseCanvas | undefined

    if (!canvases?.length) {
      canvas = await supabaseConnector.createCanvas()
    } else {
      canvas = canvases[0]
    }

    setCanvas(canvas)

    if (!canvas) {
      return
    }

    setCanvasName(canvas?.name || `Canvas-${canvas?.id}`)

    supabaseConnector.setCanvasId(canvas?.id!)

    const response = await supabaseConnector.fetchData()

    if (!response) {
      return
    }

    const { sheets, logics, edges } = response

    if (!sheets) {
      return
    }

    const measuredSheets = sheets.map((sheet) => {
      const numColumns = sheet.data.columns.length
      const numRows = sheet.data.cells.length / numColumns

      return {
        ...sheet,
        height: (numRows + 3) * 40, // num rows + 2 for header and labels rows + 1 for extra gap
        width: numColumns * 250,
      }
    })

    setNodes([...measuredSheets, ...logics])
    setEdges(edges)

    const decodedToken = jwtDecode<{ customer_id: string }>(
      session?.access_token!
    )

    const customerId = decodedToken?.customer_id!

    const customerSubscription =
      await supabaseConnector.getCustomerSubscription(customerId)

    customerSubscription && setCustomerSubscription(customerSubscription)

    let llmUsage

    if (customerId) {
      llmUsage = await supabaseConnector.getLLMUsage(customerId)
    }

    llmUsage && setLLMUsage(llmUsage)

    if (customerSubscription) {
      const subscriptionTier = await supabaseConnector.getSubscriptionTier(
        customerSubscription.subscription_tier_id
      )

      subscriptionTier && setSubscriptionTier(subscriptionTier)
    }

    let canvasSettings = null

    if (canvas) {
      const userSettingsResponse = await supabase
        .from("user_settings")
        .select()
        .eq("canvas_id", canvas.id)
        .eq("user_id", session.user.id)

      canvasSettings = userSettingsResponse.data
    }

    if (canvasSettings?.length) {
      const { canvas_x, canvas_y, canvas_zoom } = canvasSettings[0]
      const {
        x: defaultX,
        y: defaultY,
        zoom: defaultZoom,
      } = getCenteredViewport()

      setDefaultViewport({
        x: canvas_x ?? defaultX,
        y: canvas_y ?? defaultY,
        zoom: canvas_zoom ?? defaultZoom,
      })
    } else {
      setDefaultViewport(getCenteredViewport())
    }

    if (customerId) {
      const customer = await supabaseConnector.fetchCustomerData(customerId)
      if (customer) {
        setCustomer(customer)
        setEnabledFeatures(customer.enabled_features)
      }

      const customerMembership =
        await supabaseConnector.fetchCustomerMembershipData(customerId)

      if (customerMembership?.user_type) {
        setUserType(customerMembership.user_type)
      }
    }

    let userProfileData = null

    if (session?.user.id) {
      const userProfileResponse = await supabase
        .from("user_profile")
        .select()
        .eq("user_id", session.user.id)

      userProfileData = userProfileResponse.data
    }

    if (userProfileData?.length) {
      setuserProfile({
        firstName: userProfileData[0].first_name || "",
        lastName: userProfileData[0].last_name || "",
        picture: userProfileData[0].picture || "",
      })
    }

    setInitialized(true)
  }

  const [sheetValidationErrors, setSheetValidationErrors] =
    useState<SheetValidationType>({})

  const handleRemoveEdge = useCallback(
    async (edgeProps: EdgeProps<UIEdge>) => {
      setMultiplayerSelectionState((prevState) => {
        const updatedState = { ...prevState }

        Object.keys(updatedState).forEach((key) => {
          const { selectedEdges } = updatedState[key]

          if (selectedEdges.includes(edgeProps.id)) {
            updatedState[key].selectedEdges = selectedEdges.filter(
              (edge) => edge !== edgeProps.id
            )
          }
        })

        return updatedState
      })

      await removeEdgeConnection(
        edgeProps,
        edges,
        setEdges,
        nodes,
        setNodes,
        supabaseConnector
      )
    },
    [nodes, edges, supabaseConnector, session]
  )

  const handleRemoveCells = useCallback(
    async (
      sheet: UISheet,
      removedCells: UICell[],
      options?: RemovesCellsOptions
    ) => {
      const removedCellsProps = removeCells(
        // TODO: fix FlowChart types
        // @ts-ignore
        nodes,
        edges,
        sheet,
        removedCells,
        options
      )

      setEdges((edges) =>
        edges
          .filter(
            ({ data }) => !removedCellsProps.removedEdgesIds?.includes(data?.id)
          )
          .map(
            (edge) =>
              removedCellsProps.updatedEdges.find(
                (updatedEdge) => updatedEdge.id === edge.id
              ) || edge
          )
      )

      setNodes(removedCellsProps.updatedNodes)

      const { removedSheetId, removedEdgesIds, removedLogicsIds } =
        removedCellsProps

      setMultiplayerSelectionState((prevState) => {
        const updatedState = { ...prevState }

        Object.keys(updatedState).forEach((key) => {
          const { selectedNodes, selectedEdges } = updatedState[key]

          if (removedSheetId && selectedNodes.includes(removedSheetId)) {
            updatedState[key].selectedNodes = selectedNodes.filter(
              (node) => node !== removedSheetId
            )
          }

          if (removedEdgesIds?.length) {
            updatedState[key].selectedEdges = selectedEdges.filter(
              (edge) => !removedEdgesIds.includes(edge)
            )
          }

          if (removedLogicsIds?.length) {
            updatedState[key].selectedNodes = selectedNodes.filter(
              (node) => !removedLogicsIds.includes(node)
            )
          }
        })

        return updatedState
      })

      setPendingApiResponse(true)

      await supabaseConnector.handleRemoveCells(removedCellsProps)

      setPendingApiResponse(false)
    },
    [nodes, edges, session]
  )

  const handleNodeDrag = useCallback(() => {
    if (!isDraggingNode.current) {
      isDraggingNode.current = true
    }
  }, [])

  const handleNodeDragStop = useCallback(
    async (event: React.MouseEvent, node: Node) => {
      if (!isDraggingNode.current || !node) {
        return
      }
      const nodes = reactFlowRef.current?.getNodes() || []
      const nodesMap = nodes.reduce((acc, node) => {
        acc[node.id] = node
        return acc
      }, {} as Record<string, Node>)

      isDraggingNode.current = false

      const nodeClusters = clusters[node.id]

      if (isSelectionLocked && nodeClusters?.length > 1) {
        for (const clusterNode of nodeClusters) {
          await supabaseConnector.updateNode(nodesMap[clusterNode.id])
        }
      } else {
        const updatedNode = getDraggedNodePosition(node)

        setNodes(
          nodes.map((prevNode) =>
            prevNode.id !== node.id ? prevNode : updatedNode
          )
        )

        if (!session) {
          return
        }

        await supabaseConnector.updateNode(updatedNode)
      }
    },
    [clusters, isSelectionLocked, session]
  )

  const handleUpdateLogic = useCallback(
    (logicData: DatabaseLogic) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.type !== "logic" || node.data.id !== logicData.id) {
            return node
          }

          return {
            ...node,
            data: {
              ...node.data,
              logicType: logicData.logic_type as string,
              name: logicData.logic_type === UILogicType.AND ? "AND" : "OR",
            },
          }
        })
      )
    },
    [setNodes]
  )

  const [pendingSheetSelectionId, setPendingSheetSelectionId] = useState<
    string | null
  >(null)

  const handleHighlightedPathDestCellIdChange = (cellId?: string) => {
    if (cellId) {
      setHighlightedPathCellIds(cellsDependencyTree[cellId]?.cells || [])
      setHighlightedPathLogicIds(cellsDependencyTree[cellId]?.logics || [])
    } else {
      setHighlightedPathCellIds([])
      setHighlightedPathLogicIds([])
    }
  }

  const [highlightedPathCellIds, setHighlightedPathCellIds] = useState<
    string[]
  >([])

  const [highlightedPathLogicIds, setHighlightedPathLogicIds] = useState<
    string[]
  >([])

  const handleAddSheet = async (annotationType: UISheetType) => {
    const addedSheet = await addSheet(
      annotationType,
      setNodes,
      reactFlowRef,
      supabaseConnector,
      canvas?.id!
    )

    setPendingSheetSelectionId(addedSheet.id)
  }

  const handleAddConditionBlock = async () => {
    await addConditionBlock(nodes, setNodes, reactFlowRef, supabaseConnector)
  }

  const handlePublishToFirebase = async (
    selectedApis?: Record<string, boolean>
  ) => {
    const api = supabaseConnector.getAPI(
      // TODO: fix FlowChart types
      // @ts-ignore
      nodes,
      edges,
      sheetFields,
      selectedApis
    )

    const apiFuntion = getFirebaseTemplate(api)

    navigator.clipboard.writeText(apiFuntion)

    alert("Firebase function code is copied to the clipboard.")
  }

  const handlePublish = async (
    deploymentEnv: string,
    selectedApis?: Record<string, boolean>
  ) => {
    const decodedToken = jwtDecode<{ customer_name: string }>(
      session?.access_token!
    )

    const customerTeam = decodedToken?.customer_name!

    await supabaseConnector.publishAPI(
      nodes,
      edges,
      canvas?.id!,
      deploymentEnv,
      selectedApis,
      sheetFields,
      customerTeam
    )
  }

  const apiPreview = useMemo(() => {
    return api?.map((apiItem) => ({
      sheetId: apiItem.sheetId,
      name: apiItem.sheetName,
      sheetData: nodes.find((node) => node.data.id === apiItem.sheetId),
      requestPayload: {
        inputData: getApiParams(apiItem, sheetFields),
        apiSheetId: apiItem.sheetId,
      },
      responseKeys: apiItem.cells
        .filter((cell) => cell.prev)
        .map((cell) => cell.name),
    }))
  }, [api, nodes])

  const handleViewAndDeployAPI = async () => {
    if (!isApiPreviewVisible) {
      // TODO: fix FlowChart types
      // @ts-ignore
      const api = supabaseConnector.getAPI(nodes, edges, sheetFields)
      setApi(api)
    }
    setIsApiPreviewVisible(!isApiPreviewVisible)
  }

  const [pendingExtendSheetParams, setPendingExtendSheetParams] = useState<{
    sheetId: string
    rowIndex?: number
    columnIndex?: number
  }>()

  const [canvasName, setCanvasName] = useState("")

  useEffect(() => {
    if (!isPendingNodesUpdate && pendingExtendSheetParams) {
      const { sheetId, rowIndex, columnIndex } = pendingExtendSheetParams

      setPendingExtendSheetParams(undefined)

      if (rowIndex) {
        handleAddRow(sheetId, rowIndex)
      } else if (columnIndex) {
        handleAddColumn(sheetId, columnIndex)
      }
    }
  }, [isPendingNodesUpdate, pendingExtendSheetParams])

  const handleAddRow = async (sheetId: string, rowIndex: number) => {
    await addRow(
      sheetId,
      rowIndex,
      nodes,
      edges,
      setNodes,
      setEdges,
      setPendingApiResponse,
      isPendingNodesUpdate,
      setPendingExtendSheetParams,
      supabaseConnector
    )
  }

  const refreshApiPreview = () => {
    refreshApiPreviewTimeout.current = setTimeout(() => {
      if (!reactFlowRef.current) {
        return
      }

      try {
        const api = supabaseConnector.getAPI(
          reactFlowRef.current.getNodes(),
          reactFlowRef.current.getEdges(),
          sheetFields
        )
        const cellsDependencyTree = buildCellsDependencyTree(api)

        setCellsDependencyTree(cellsDependencyTree)
        setApi(api)
      } catch (e) {
        // TODO: check if we need to call refreshApiPreview() here
        refreshApiPreview()
        console.log(e, "Warning: API live preview error.")
      }
    }, 1000)
  }

  useEffect(() => {
    if (isPendingApiResponse) {
      return
    }

    refreshApiPreview()

    return () => {
      clearTimeout(refreshApiPreviewTimeout.current)
    }
  }, [nodes, edges, sheetFields, isPendingApiResponse])

  const handleAddCells = async (
    sheetId: string,
    cells: UICell[],
    nodes: Node[]
  ) => {
    const columns: UIColumn[] = []
    const existingColumns: Record<string, boolean> = {}
    const sheetColumns = (
      nodes.find((node) => node.data.id === sheetId)?.data as UISheet
    ).columns

    sheetColumns.forEach((column: UIColumn) => {
      existingColumns[column.columnIndex] = true
    })

    cells.forEach((cell) => {
      if (!existingColumns[cell.columnIndex]) {
        columns.push({
          id: uuidv7(),
          columnIndex: cell.columnIndex,
          sheetId: sheetId,
          width: CELL_WIDTH,
        })

        existingColumns[cell.columnIndex] = true
      }
    })

    await addCells(sheetId, cells, columns, nodes, setNodes, supabaseConnector)
  }

  const handleAddColumn = async (sheetId: string, columnIndex: number) => {
    await addColumn(
      sheetId,
      columnIndex,
      nodes,
      setNodes,
      isPendingNodesUpdate,
      setPendingApiResponse,
      setPendingExtendSheetParams,
      supabaseConnector
    )
  }

  const handleRemoveRow = (sheetId: string, rowIndex: number) => {
    const sheet: UISheet = nodes.find((node) => node.data.id === sheetId)
      ?.data as UISheet

    if (!sheet) {
      return Promise.resolve()
    }

    const removedCells = sheet.cells.filter(
      (cell) => cell.rowIndex === rowIndex
    )

    handleRemoveCells(sheet, removedCells, { rowIndex })

    return Promise.resolve()
  }

  const handleRemoveColumn = async (sheetId: string, columnIndex: number) => {
    const sheet: UISheet = nodes.find((node) => node.data.id === sheetId)
      ?.data as UISheet

    if (!sheet) {
      return Promise.resolve()
    }

    const removedCells = sheet.cells.filter(
      (cell) => cell.columnIndex === columnIndex
    )

    await handleRemoveCells(sheet, removedCells, { columnIndex })

    return Promise.resolve()
  }

  const handleUpdateCell = async (
    updatedCellValue: string,
    cellId: string,
    sheetId: string
  ) => {
    if (reactFlowRef.current) {
      const sheet = reactFlowRef.current.getNode(sheetId)

      setValidationErrors((validationErrors) => {
        if (validationErrors[sheet?.data.id]?.[cellId]) {
          const cell = (sheet?.data as UISheet).cells.find(
            (cell) => cell.id === cellId
          )

          let canBeEvaluated = !!updatedCellValue.match(comparisonFormulaRegExp)

          if (!canBeEvaluated) {
            canBeEvaluated = cell?.name && sheet?.data.columns[cell.columnIndex]
          }

          return {
            ...validationErrors,
            [sheet?.data.id]: {
              ...validationErrors[sheet?.data.id],
              [cellId]: canBeEvaluated ? "" : CELL_CANNOT_BE_EVALUATED,
            },
          }
        }

        return validationErrors
      })
    }

    setNodes((nodes) =>
      nodes.map((node) => {
        return node.id === sheetId
          ? {
              ...node,
              data: {
                ...node.data,
                cells: (node.data as UISheet).cells.map((cell) =>
                  cell.id === cellId
                    ? {
                        ...cell,
                        name: updatedCellValue,
                      }
                    : cell
                ),
              },
            }
          : node
      })
    )

    await callSyncData()

    return await supabaseConnector.updateCellValue(updatedCellValue, cellId)
  }

  const handleUpdateSheet = async (sheet: UISheet) => {
    return await supabaseConnector.updateSheetName(sheet)
  }

  const handleUpdateColumn = async (sheetId: string, column: UIColumn) => {
    if (!canvas) {
      return
    }

    setPendingNodesUpdate(true)

    await updateColumn(sheetId, column, setNodes)

    setPendingNodesUpdate(false)
  }

  const handleUpdateRowLabel = async (
    sheetId: string,
    rowIndex: number,
    text: string
  ) => {
    if (!canvas) {
      return
    }

    setPendingNodesUpdate(true)

    await updateRowLabel(sheetId, rowIndex, text, setNodes, canvas.id)

    setPendingNodesUpdate(false)
  }

  const [isLoadingAIResponse, setIsLoadingAIResponse] = useState(false)
  const [matrixLabel, setMatrixLabel] = useState("")
  const [editedMatrixProps, setEditedMatrixProps] = useState<
    | {
        id: string
        labelType: string
      }
    | undefined
  >()

  const handleOpenMatrixLabelEditor = (
    matrixId: string,
    labelType: string,
    labelText?: string
  ) => {
    setEditedMatrixProps({ id: matrixId, labelType })
    setMatrixLabel(labelText || "")
    setMatrixLabelEditorOpen(true)
    setTimeout(() => {
      matrixLabelInputRef?.current?.focus()
    }, 200)
  }

  const [multiselectedCells, setMultiselectedCells] =
    useState<MultiselectedCells>(null)

  const handleDeleteAllSelected = () => {
    if (!reactFlowRef.current) {
      return
    }

    const nodes = reactFlowRef.current?.getNodes()
    const edges = reactFlowRef.current?.getEdges()
    let nodesToRemove = nodes.filter((node) => node.selected)

    const nodesToRemoveIdsMap = nodesToRemove.reduce((acc, { id }) => {
      acc[id] = true
      return acc
    }, {} as Record<string, boolean>)

    let edgesToRemove = edges.filter(
      (edge) =>
        nodesToRemoveIdsMap[edge.target] || nodesToRemoveIdsMap[edge.source]
    )

    const edgesToRemoveIdsMap = edgesToRemove.reduce((acc, { id }) => {
      acc[id] = true
      return acc
    }, {} as Record<string, boolean>)

    const nonSelectedSourceLogics = nodes.filter((node) => {
      if (
        node.type === "logic" &&
        !nodesToRemoveIdsMap[node.id] &&
        edgesToRemove.some((edge) => edge.source === node.id)
      ) {
        nodesToRemoveIdsMap[node.id] = true
        nodesToRemove.push(node)
        return true
      }

      return false
    })

    edges.forEach((edge) => {
      if (nonSelectedSourceLogics.some((logic) => logic.id === edge.target)) {
        edgesToRemoveIdsMap[edge.id] = true
        edgesToRemove.push(edge)
      }
    })

    const nonSelectedTargetLogics = nodes.filter(
      (node) =>
        node.type === "logic" &&
        !nodesToRemoveIdsMap[node.id] &&
        edgesToRemove.some((edge) => edge.target === node.id)
    )

    const logicsToDeleteOutsideSelection: any = []
    const logicsToDeleteOutsideSelectionMap: Record<string, Node> = {}
    const edgesToReconnect: Record<
      string,
      { incomingEdge: Edge; outgoingEdge: Edge }
    > = {}

    nonSelectedTargetLogics.forEach((logic) => {
      const incomingEdges = edges.filter((edge) => edge.target === logic.id)

      const remainingIncomingEdges = incomingEdges.filter(
        (edge) => !edgesToRemoveIdsMap[edge.id]
      )

      if (remainingIncomingEdges.length === 0) {
        logicsToDeleteOutsideSelection.push(logic.id)
        logicsToDeleteOutsideSelectionMap[logic.id] = logic
        nodesToRemove.push(logic)
        const outgoingEdge = edges.find(
          (edge) => edge.source === logic.id && !edgesToRemoveIdsMap[edge.id]
        )
        if (outgoingEdge) {
          edgesToRemoveIdsMap[outgoingEdge.id] = true
          edgesToRemove.push(outgoingEdge)
        }
      }

      if (remainingIncomingEdges.length === 1) {
        const outgoingEdge = edges.find(
          (edge) => edge.source === logic.id && !edgesToRemoveIdsMap[edge.id]
        )!

        edgesToRemoveIdsMap[outgoingEdge.id] = true
        edgesToRemove.push(outgoingEdge)
        const incomingEdge = remainingIncomingEdges[0]

        nodesToRemoveIdsMap[logic.id] = true
        nodesToRemove.push(logic)
        edgesToReconnect[incomingEdge.id] = { incomingEdge, outgoingEdge }
      }
    })

    setNodes((nodes) =>
      nodes.filter((node) => {
        return (
          !nodesToRemoveIdsMap[node.id] &&
          !logicsToDeleteOutsideSelection.includes(node.id)
        )
      })
    )

    setEdges((edges) =>
      edges
        .filter((edge) => {
          return (
            !edgesToRemoveIdsMap[edge.id] &&
            !logicsToDeleteOutsideSelectionMap[edge.target]
          )
        })
        .map((edge) => {
          if (edgesToReconnect[edge.id]) {
            return {
              ...edge,
              target: edgesToReconnect[edge.id]!.outgoingEdge.target,
              targetHandle:
                edgesToReconnect[edge.id]!.outgoingEdge.targetHandle,
            }
          }
          return edge
        })
    )

    supabaseConnector.removeNodesAndEdges(
      nodesToRemove
        .filter((node) => node.type !== "logic")
        .map(({ data }) => data.id),
      nodesToRemove
        .filter((node) => node.type === "logic")
        .map(({ data }) => data.id),
      edgesToRemove.map(({ data }) => data.id),
      Object.values(edgesToReconnect).map(({ incomingEdge, outgoingEdge }) => ({
        id: incomingEdge.data.id,
        dest_sheet_id: outgoingEdge.data.targetSheetId,
        dest_row_index: outgoingEdge.data.targetRowIndex,
      }))
    )
  }

  const handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key?.toLowerCase() === "enter" && isMatrixLabelEditorOpen) {
      handleConfirmMatrixLabel()
    }

    if (event.key?.toLowerCase() === "delete" && multiselectedCells) {
      const updatedNodes = nodes.map((node) => {
        if (node.type === "sheet" && multiselectedCells?.sheetId === node.id) {
          const updatedCells = (node.data as UISheet).cells.map((cell) => {
            if (
              cell.rowIndex >= multiselectedCells.startRow &&
              cell.rowIndex <= multiselectedCells.endRow &&
              cell.columnIndex >= multiselectedCells.startColumn &&
              cell.columnIndex <= multiselectedCells.endColumn
            ) {
              return { ...cell, name: "" }
            }
            return cell
          })

          return {
            ...node,
            data: {
              ...node.data,
              cells: updatedCells,
            },
          }
        }
        return node
      })

      setNodes(updatedNodes)

      // TODO: implement clearCells function
      // await supabaseConnector.clearCells(multiselectedCells)
      setMultiselectedCells(null)
    }

    if (event.key?.toLowerCase() === "a" && event.altKey) {
      event.preventDefault()
      setAutogenerateDialogOpen(true)
    }

    if (event.key?.toLowerCase() === "s" && event.altKey) {
      event.preventDefault()
      await handleAddSheet(UISheetType.SHEET)
    }

    if (event.key?.toLowerCase() === "backspace" && !editedCell) {
      event.preventDefault()

      handleDeleteAllSelected()
    }
  }

  const [editedCell, setEditedCell] = useState<UICell>()

  useEffect(() => {
    document.addEventListener("keyup", handleKeyDown)

    return () => {
      document.removeEventListener("keyup", handleKeyDown)
    }
  }, [
    isMatrixLabelEditorOpen,
    editedMatrixProps,
    matrixLabel,
    multiselectedCells,
    editedCell,
  ])

  const handleConfirmMatrixLabel = async () => {
    await confirmMatrixLabel(
      matrixLabel,
      editedMatrixProps,
      setNodes,
      setMatrixLabelEditorOpen,
      setMatrixLabel,
      supabaseConnector
    )
  }

  const handleAutogenerateCanvas = async (
    autogeneratePrompt: string,
    filesToUpload: File[]
  ) => {
    try {
      setIsLoadingAIResponse(true)

      const autogenResult = await autogenerateCanvas(
        autogeneratePrompt,
        filesToUpload,
        canvas?.id!,
        setNodes,
        supabaseConnector,
        reactFlowRef
      )

      if (!autogenResult?.uiEntities) {
        return
      }

      let arrangedAutogenEntities = autogenResult?.uiEntities!

      const { importedSheets, importedEdges, importedLogics } =
        arrangedAutogenEntities

      setNodes((nodes) => [...nodes, ...importedSheets, ...importedLogics])
      setEdges((edges) => [...edges, ...importedEdges])

      const { sheetsData, cellsData, edgesData, logicsData, columnLabelsData } =
        autogenResult!.dbEntities

      await supabaseConnector.importCanvas(
        sheetsData,
        cellsData,
        edgesData,
        logicsData,
        columnLabelsData
      )

      setAutogenerateResult(autogenResult!.dbEntities)

      setAutogenerateResultsDialogOpen(true)

      syncupLLMUSageTimeout.current = setTimeout(async () => {
        if (customer) {
          const llmUsageData = await supabaseConnector.getLLMUsage(customer.id)
          llmUsageData && setLLMUsage(llmUsageData)
        }
      }, 5000)
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoadingAIResponse(false)
      setAutogenerateDialogOpen(false)
    }
  }

  const handleUpdateSheetName = async (sheet: UISheet, value: string) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.data.id === sheet.id
          ? { ...node, data: { ...node.data, name: value } }
          : node
      )
    )

    await supabaseConnector.updateSheetName(sheet)
  }

  const handleUpdateColumnLabelsLabel = async (
    sheet: UISheet,
    value: string
  ) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.data.id === sheet.id
          ? { ...node, data: { ...node.data, columnLabelsLabel: value } }
          : node
      )
    )

    await supabaseConnector.updateColumnLabelsLabel(sheet.id, value)
  }

  const handleUpdateRowLabelsLabel = async (sheet: UISheet, value: string) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.data.id === sheet.id
          ? { ...node, data: { ...node.data, rowLabelsLabel: value } }
          : node
      )
    )

    await supabaseConnector.updateRowLabelsLabel(sheet.id, value)
  }

  const handleCanvasNameChange = async (value: string) => {
    await supabase.from("canvas").update({ name: value }).eq("id", canvas?.id!)
  }

  const [selectedCell, setSelectedCell] = useState<UICell>()
  const [isAutogenerateDialogOpen, setAutogenerateDialogOpen] = useState(false)
  const [isAutogenerateResultsDialogOpen, setAutogenerateResultsDialogOpen] =
    useState(false)
  const [autogenerateResult, setAutogenerateResult] = useState<any>(undefined)

  const handleCellClick = (cell: UICell) => {
    if (selectedCell?.id === cell.id) {
      setEditedCell(cell)
    } else {
      setSelectedNodes([`sheet-${cell.sheetId}`])
      setEditedCell(cell)
      setSelectedCell(cell)
    }
  }

  const pasteEventListener = (event: any) => {
    if (!selectedCell || selectedCell.id === editedCell?.id) {
      return
    }

    const pastedText = event.clipboardData?.getData("text")
    // Remove \r to handle cases \r\n and \n in the same way.
    const rows = pastedText?.replaceAll("\r", "").split("\n")
    const pastedCells: Record<string, string> = {}
    rows.forEach((row: string, rowIndex: number) => {
      row.split("\t").forEach((text: string, columnIndex: number) => {
        pastedCells[
          `${selectedCell.rowIndex + rowIndex}-${
            selectedCell.columnIndex + columnIndex
          }`
        ] = text
      })
    })

    handlePaste(selectedCell.sheetId, pastedCells)
  }

  useEffect(() => {
    window.addEventListener("paste", pasteEventListener)

    return () => window.removeEventListener("paste", pasteEventListener)
  }, [selectedCell, editedCell])

  const [multiplayerState, setMultiplayerState] = useState<
    Record<string, MultiplayerState>
  >({})

  const [multiplayerSelectionState, setMultiplayerSelectionState] = useState<
    Record<string, MultiplayerSelectionState>
  >({})

  const multiplayerSelectedNodes = useMemo(() => {
    let selectedNodesFromAllUsers: Record<string, string> = {}

    Object.values(multiplayerSelectionState).forEach(
      ({ userName, selectedNodes, selectedEdges }) => {
        selectedNodes.forEach((nodeId) => {
          selectedNodesFromAllUsers[nodeId] = userName

          edges.forEach((edge) => {
            if (edge.source === nodeId) {
              selectedNodesFromAllUsers[edge.target] = userName
            }

            if (edge.target === nodeId) {
              selectedNodesFromAllUsers[edge.source] = userName
            }

            if (edge.source.startsWith("logic-") && edge.target === nodeId) {
              const logicIncomingEdge = edges.find(
                (e) => e.target === edge.source
              )

              if (logicIncomingEdge) {
                selectedNodesFromAllUsers[logicIncomingEdge.source] = userName
                selectedNodesFromAllUsers[logicIncomingEdge.target] = userName
              }
            }

            if (edge.target.startsWith("logic-") && edge.source === nodeId) {
              const logicOutgoingEdge = edges.find(
                (e) => e.source === edge.target
              )

              if (logicOutgoingEdge) {
                selectedNodesFromAllUsers[logicOutgoingEdge.source] = userName
                selectedNodesFromAllUsers[logicOutgoingEdge.target] = userName
              }
            }
          })
        })

        selectedEdges.forEach((edgeId) => {
          const edge = edges.find((edge) => edge.id === edgeId)

          if (edge) {
            selectedNodesFromAllUsers[edge.source] = userName
            selectedNodesFromAllUsers[edge.target] = userName
          }

          if (edge && edge.source.startsWith("logic-")) {
            const logicIncomingEdge = edges.find(
              (e) => e.target === edge.source
            )

            if (logicIncomingEdge) {
              selectedNodesFromAllUsers[logicIncomingEdge.source] = userName
              selectedNodesFromAllUsers[logicIncomingEdge.target] = userName
            }
          }

          if (edge && edge.target.startsWith("logic-")) {
            const logicOutgoingEdge = edges.find(
              (e) => e.source === edge.target
            )

            if (logicOutgoingEdge) {
              selectedNodesFromAllUsers[logicOutgoingEdge.source] = userName
              selectedNodesFromAllUsers[logicOutgoingEdge.target] = userName
            }
          }
        })
      }
    )

    return selectedNodesFromAllUsers
  }, [multiplayerSelectionState])

  const multiplayerSelectedEdges = useMemo(() => {
    let selectedEdgesFromAllUsers: Record<string, string> = {}

    Object.values(multiplayerSelectionState).forEach(
      ({ userName, selectedEdges, selectedNodes }) => {
        selectedEdges.forEach((edeId) => {
          selectedEdgesFromAllUsers[edeId] = userName
        })

        selectedNodes.forEach((nodeId) => {
          edges.forEach((edge) => {
            if (edge.source === nodeId) {
              selectedEdgesFromAllUsers[edge.id] = userName
            }

            if (edge.target === nodeId) {
              selectedEdgesFromAllUsers[edge.id] = userName
            }
          })
        })

        Object.keys(selectedEdgesFromAllUsers).forEach((edgeId) => {
          const edge = edges.find((edge) => edge.id === edgeId)

          if (edge && edge.source.startsWith("logic-")) {
            const logicIncomingEdge = edges.find(
              (e) => e.target === edge.source
            )

            if (logicIncomingEdge) {
              selectedEdgesFromAllUsers[logicIncomingEdge.id] = userName
            }
          }

          if (edge && edge.target.startsWith("logic-")) {
            const logicOutgoingEdge = edges.find(
              (e) => e.source === edge.target
            )

            if (logicOutgoingEdge) {
              selectedEdgesFromAllUsers[logicOutgoingEdge.id] = userName
            }

            const logicIncomingEdges = edges.filter(
              (e) => e.target === edge.target
            )
            logicIncomingEdges.forEach((logicIncomingEdge) => {
              selectedEdgesFromAllUsers[logicIncomingEdge.id] = userName
            })
          }
        })
      }
    )

    return selectedEdgesFromAllUsers
  }, [multiplayerSelectionState, edges])

  const handleEdgeClick = (event: React.MouseEvent, edge: Edge) => {
    if (multiplayerSelectedEdges[edge.id]) {
      return
    }

    setSelectedEdges([edge.id])
  }

  const handleConditionResize = async (flowChartCondition: UISheet) => {
    await resizeFlowchartCondition(
      flowChartCondition,
      setNodes,
      supabaseConnector
    )
  }

  const handlePaste = (
    sheetId: string,
    pastedCells: Record<string, string>
  ) => {
    pasteCells(sheetId, pastedCells, setNodes, supabaseConnector)
  }

  const appContainerRef = useRef<HTMLDivElement>(null)

  const [cellWithContextMenu, setCellWithContextMenu] = useState<
    CellWithContextMenuInfoType | undefined
  >(undefined)

  const [showMinimap, setShowMinimap] = useState(false)
  const [isManageSubscriptionModalOpen, setManageSubscriptionModalOpen] =
    useState(false)

  const [isProfileModalOpen, setProfileModalOpen] = useState(false)

  const updateClusters = () => {
    const clusters: Record<string, Node<UISheet | UILogic>[]> = {}

    getClusters(nodes, edges).forEach((cluster) => {
      cluster.forEach((node) => {
        clusters[node.id] = cluster
      })
    })

    setClusters(clusters)
  }

  useEffect(() => {
    if (isSelectionLocked) {
      updateClusters()
    }
  }, [nodes, edges, isSelectionLocked])

  useEffect(() => {
    if (triggerSyncData.current) {
      if (isSyncDataNeeded.current) {
        triggerSyncData.current()
      } else {
        isSyncDataNeeded.current = true
      }
    }
  }, [nodes, edges])

  if (session === null) {
    window.location.assign(getFullURL("login"))
  }

  const handleColumnOrderChange = (
    sheetId: string,
    draggedColumnIndex: number,
    hoveredColumnIndex: number
  ) => {
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.data.id !== sheetId) {
          return node
        }

        const cells = (node.data as UISheet).cells

        const draggedCells = cells.filter(
          (cell) => cell.columnIndex === draggedColumnIndex
        )
        const hoveredCells = cells.filter(
          (cell) => cell.columnIndex === hoveredColumnIndex
        )
        const unchangedCells = cells.filter(
          (cell) =>
            cell.columnIndex !== draggedColumnIndex &&
            cell.columnIndex !== hoveredColumnIndex
        )

        const updatedCells = [
          ...draggedCells.map((cell) => ({
            ...cell,
            columnIndex: hoveredColumnIndex,
          })),
          ...hoveredCells.map((cell) => ({
            ...cell,
            columnIndex: draggedColumnIndex,
          })),
        ]

        supabaseConnector.updateCells(updatedCells)

        return {
          ...node,
          data: { ...node.data, cells: [...unchangedCells, ...updatedCells] },
        }
      })
    )
  }

  const handleRowOrderChange = (
    sheetId: string,
    draggedRowIndex: number,
    hoveredRowIndex: number
  ) => {
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.data.id !== sheetId) {
          return node
        }

        const cells = (node.data as UISheet).cells

        const draggedCells = cells.filter(
          (cell) => cell.rowIndex === draggedRowIndex
        )
        const hoveredCells = cells.filter(
          (cell) => cell.rowIndex === hoveredRowIndex
        )
        const unchangedCells = cells.filter(
          (cell) =>
            cell.rowIndex !== draggedRowIndex &&
            cell.rowIndex !== hoveredRowIndex
        )

        const updatedCells = [
          ...draggedCells.map((cell) => ({
            ...cell,
            rowIndex: hoveredRowIndex,
          })),
          ...hoveredCells.map((cell) => ({
            ...cell,
            rowIndex: draggedRowIndex,
          })),
        ]

        supabaseConnector.updateCells(updatedCells)

        return {
          ...node,
          data: { ...node.data, cells: [...unchangedCells, ...updatedCells] },
        }
      })
    )
  }

  const [validationErrors, setValidationErrors] = useState<
    Record<string, Record<string, string>>
  >({})

  const [testedApiSheetId, setTestedApiSheetId] = useState<string | null>(null)

  const testApi = (sheetId: string) => {
    // TODO: fix FlowChart types
    // @ts-ignore
    const api = supabaseConnector.getAPI(nodes, edges, sheetFields)
    setApi(api)

    updateClusters()

    setTestedApiSheetId(sheetId)
  }

  const validateApi = (sheetId: string) => {
    if (!reactFlowRef.current) {
      return
    }

    const errors = getValidationForApi(
      sheetId,
      reactFlowRef.current.getNodes(),
      reactFlowRef.current.getEdges(),
      supabaseConnector
    )

    setValidationErrors(() => {
      return {
        ...validationErrors,
        ...errors,
      }
    })
  }

  const isLLMUsageExceeded =
    llmUsage?.usage_count !== undefined &&
    subscriptionTier?.monthly_llm_limit !== undefined &&
    subscriptionTier?.monthly_llm_limit !== null &&
    llmUsage?.usage_count >= subscriptionTier?.monthly_llm_limit

  const trackMousePointer = async (event: MouseEvent) => {
    if (
      !multiplayerChannel.current ||
      !session ||
      window.location.hostname === "localhost" ||
      !reactFlowRef.current
    ) {
      return
    }

    const canvasPosition = reactFlowRef.current.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    })

    await multiplayerChannel.current.send({
      type: "broadcast",
      event: "MultiplayerCursor",
      payload: {
        userId: session.user.id,
        timestamp: Date.now(),
        mouseX: canvasPosition.x,
        mouseY: canvasPosition.y,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        userPicture: userProfile.picture,
      },
    })
  }

  const syncMultiplayerState = () => {
    setMultiplayerState((prev) => {
      const updatedState: Record<string, MultiplayerState> = {}
      const currentTime = Date.now()
      const timeout = 5000

      Object.keys(prev).forEach((userId) => {
        if (currentTime - prev[userId].timestamp <= timeout) {
          updatedState[userId] = prev[userId]
        } else {
          console.log("User timed out", userId)
        }
      })

      return updatedState
    })

    syncMultiplayerStateTimeout.current = setTimeout(syncMultiplayerState, 1000)
  }

  useEffect(() => {
    return () => {
      clearTimeout(syncMultiplayerStateTimeout.current)
    }
  }, [])

  const [userProfile, setuserProfile] = useState<UserProfile>({})
  const triggerSyncData = useRef<any>()

  const callSyncData = () =>
    multiplayerChannel.current?.send({
      type: "broadcast",
      event: "MultiplayerSyncData",
      payload: {
        userId: session?.user.id,
      },
    })

  useEffect(() => {
    const isLocalhost = window.location.hostname === "localhost"

    if (canvas && !isLocalhost) {
      multiplayerChannel.current = supabase.channel(canvas?.id!)

      triggerSyncData.current = debounce(async () => {
        await callSyncData()
      }, 200)

      multiplayerChannel.current
        .on("broadcast", { event: "MultiplayerCursor" }, (data) => {
          const payload: MultiplayerState = data.payload
          const { userId } = payload

          if (!userId || userId === session?.user.id || !reactFlowRef.current) {
            return
          }

          setMultiplayerState((prev) => {
            const updatedState = { ...prev }

            if (
              !updatedState[userId] ||
              payload.timestamp > updatedState[userId].timestamp
            ) {
              const screenCursorPos =
                reactFlowRef.current!.flowToScreenPosition({
                  x: payload.mouseX,
                  y: payload.mouseY,
                })
              updatedState[userId] = {
                ...payload,
                mouseX: screenCursorPos.x,
                mouseY: screenCursorPos.y,
              }
            }

            return updatedState
          })
        })
        .on("broadcast", { event: "selection" }, (data) => {
          const payload: MultiplayerSelectionState = data.payload
          const { userId } = payload

          if (!userId || userId === session?.user.id) {
            return
          }

          setMultiplayerSelectionState((prev) => {
            const updatedState = { ...prev }

            if (
              !updatedState[userId] ||
              payload.timestamp > updatedState[userId].timestamp
            ) {
              updatedState[userId] = payload
            }

            return updatedState
          })
        })
        .on("broadcast", { event: "MultiplayerSyncData" }, async (data) => {
          const payload: { userId: string } = data.payload
          const { userId } = payload

          if (!userId || userId === session?.user.id) {
            return
          }

          await syncData()
        })
        .subscribe()

      syncMultiplayerStateTimeout.current = setTimeout(
        syncMultiplayerState,
        1000
      )

      document.addEventListener("mousemove", trackMousePointer)
    }

    return () => {
      multiplayerChannel.current?.unsubscribe()
      multiplayerChannel.current = undefined
      document.removeEventListener("mousemove", trackMousePointer)
    }
  }, [session, canvas, userProfile])

  const sendMultiplayerSelectionState = async () => {
    if (
      !multiplayerChannel.current ||
      !session ||
      window.location.hostname === "localhost"
    ) {
      return
    }

    await multiplayerChannel.current.send({
      type: "broadcast",
      event: "selection",
      payload: {
        userId: session.user.id,
        selectedNodes,
        selectedEdges,
        selectedCell: selectedCell?.id,
        timestamp: Date.now(),
        userName: session.user.email?.split("@")[0],
      },
    })
  }

  useEffect(() => {
    if (!multiplayerChannel.current) {
      return
    }

    sendMultiplayerSelectionState()
  }, [selectedNodes, selectedEdges, selectedCell])

  useEffect(() => {
    if (selectedCell) {
      setMultiselectedCells(null)
    }
  }, [selectedCell])

  const multiplayerUserPointers = useMemo(() => {
    return Object.values(multiplayerState).filter(
      (user) => user.mouseX !== undefined && user.mouseY !== undefined
    )
  }, [multiplayerState])

  const multiplayerUsersInfo = useMemo(() => {
    return Object.values(multiplayerState).map((user) => ({
      firstName: user.firstName,
      lastName: user.lastName,
      userId: user.userId,
      userPicture: user.userPicture,
    }))
  }, [multiplayerState])

  const cellMultiselectStartCell = useRef<UICell | null>(null)

  const handleCellMouseDown = (
    cell: UICell,
    sheetRows: UICell[][],
    isShiftPressed: boolean
  ) => {
    const startCell = cellMultiselectStartCell.current as UICell

    if (!startCell || !isShiftPressed) {
      cellMultiselectStartCell.current = cell
      // setMultiselectedCells(null)
      return
    }

    if (isShiftPressed && startCell) {
      if (startCell.sheetId !== cell.sheetId) {
        cellMultiselectStartCell.current = cell
        // setMultiselectedCells(null)
        return
      }

      const endCell = cell
      const startRowIndex = Math.min(startCell.rowIndex, endCell.rowIndex)
      const endRowIndex = Math.max(startCell.rowIndex, endCell.rowIndex)
      const startColumnIndex = Math.min(
        startCell.columnIndex,
        endCell.columnIndex
      )
      const endColumnIndex = Math.max(
        startCell.columnIndex,
        endCell.columnIndex
      )

      const selectedCells: UICell[] = []

      for (let rowIndex = startRowIndex; rowIndex < endRowIndex; rowIndex++) {
        for (
          let columnIndex = startColumnIndex;
          columnIndex < endColumnIndex;
          columnIndex++
        ) {
          selectedCells.push(sheetRows[rowIndex][columnIndex])
        }
      }

      setMultiselectedCells({
        startRow: startRowIndex,
        endRow: endRowIndex,
        startColumn: startColumnIndex,
        endColumn: endColumnIndex,
        sheetId: cell.sheetId,
      })
      setSelectedCell(undefined)
      setEditedCell(undefined)
    }
  }

  const [hoveredCell, setHoveredCell] = useState<string | undefined>()
  const [nodesSelectionState, setNodesSelectionState] = useState<Node[]>([])

  const dragEnabled = useMemo(() => {
    return !(
      (editedCell && editedCell?.id === hoveredCell) ||
      isEditingLabel ||
      isDraggingColumnOrRow
    )
  }, [editedCell, hoveredCell, isEditingLabel, isDraggingColumnOrRow])

  const isEditingAllowed = userType === "PMBiz" || userType === "Superuser"

  if (session === undefined || !isInitialized) {
    return (
      <div className="loading-indicator">
        <span className="loading-indicator-span-1"></span>
        <span className="loading-indicator-span-2"></span>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <ToolBar
        handleAddConditionBlock={handleAddConditionBlock}
        handleAddSheet={handleAddSheet}
        isLLMUsageExceeded={isLLMUsageExceeded}
        setAutogenerateResult={setAutogenerateResult}
        setAutogenerateDialogOpen={setAutogenerateDialogOpen}
        userType={userType}
        setUserType={setUserType}
        enabledFeatures={customer?.enabled_features}
      />
      {multiselectControls && (
        <MultiselectToolbar
          x={multiselectControls.x}
          y={multiselectControls.y}
          onClick={handleDeleteAllSelected}
        />
      )}
      <AppContext.Provider
        value={{
          setHoveredCell,
          onPasteCells: handlePaste,
          onCellMouseDown: handleCellMouseDown,
          isDraggingNode: isDraggingNode.current,
          multiplayerSelectedNodes,
          multiplayerSelectedEdges,
          validationErrors,
          selectedNodes,
          selectedEdges,
          setSelectedNodes,
          removeCells: handleRemoveCells,
          removeEdge: handleRemoveEdge,
          sheetFields,
          updateLogic: handleUpdateLogic,
          updateCell: handleUpdateCell,
          updateSheet: handleUpdateSheet,
          connectionStart,
          onConnect: handleConnect,
          onAddRow: handleAddRow,
          onAddColumn: handleAddColumn,
          onAddCells: handleAddCells,
          onRemoveRow: handleRemoveRow,
          onRemoveColumn: handleRemoveColumn,
          onUpdateColumn: handleUpdateColumn,
          onUpdateRowLabel: handleUpdateRowLabel,
          setIsEditingLabel,
          setIsDraggingColumnOrRow,
          handleColumnOrderChange,
          handleRowOrderChange,
          isPendingApiResponse,
          isSheetsUIVisible,
          pendingSheetSelectionId,
          setPendingSheetSelectionId,
          setHighlightedPathDestCellId: handleHighlightedPathDestCellIdChange,
          highlightedPathCellIds,
          highlightedPathLogicIds,
          onUpdateSheetName: handleUpdateSheetName,
          onUpdateColumnLabelsLabel: handleUpdateColumnLabelsLabel,
          onUpdateRowLabelsLabel: handleUpdateRowLabelsLabel,
          sheetValidationErrors,
          onPaste: handlePaste,
          onCellClick: handleCellClick,
          selectedCell: selectedCell,
          editedCell: editedCell,
          setEditedCell: setEditedCell,
          onOpenMatrixLabelEditor: handleOpenMatrixLabelEditor,
          cellWithContextMenu,
          setCellWithContextMenu,
          handleConditionResize,
          setSelectedCell,
          multiselectedCells,
        }}
      >
        <ReactFlow
          ref={appContainerRef}
          // selectionKeyCode={null}
          translateExtent={[
            [0, 0],
            [CANVAS_SIZE, CANVAS_SIZE],
          ]}
          nodeExtent={[
            [0, 0],
            [CANVAS_SIZE, CANVAS_SIZE],
          ]}
          onMove={(_, viewport: Viewport) => {
            handleViewportChange(viewport)
          }}
          defaultViewport={defaultViewport}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgeClick={handleEdgeClick}
          onConnect={handleConnect}
          onInit={handleInit}
          proOptions={{ hideAttribution: true }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          minZoom={0.1}
          deleteKeyCode={null}
          zoomOnDoubleClick={false}
          panOnDrag={dragEnabled}
          nodesDraggable={dragEnabled && isEditingAllowed}
          elementsSelectable={isEditingAllowed}
          onNodeDrag={handleNodeDrag}
          onNodeDragStop={handleNodeDragStop}
          connectionLineComponent={EdgePreview}
          onConnectStart={(_, { nodeId, handleId }) =>
            setConnectionStart({ source: nodeId, sourceHandle: handleId })
          }
          onConnectEnd={() => setConnectionStart(undefined)}
          onPaneClick={() => {
            if (selectedNodes.length) {
              setSelectedNodes([])
              setSelectedCell(undefined)
              setEditedCell(undefined)
              setCellWithContextMenu(undefined)
              setMultiselectedCells(null)
              cellMultiselectStartCell.current = null
            }

            if (selectedEdges.length) {
              setSelectedEdges([])
            }
          }}
          onNodeClick={(_, node) => {
            setSelectedNodes([node.id])
          }}
          onSelectionChange={(selection) => {
            setNodesSelectionState(selection.nodes)
            // console.log(getNodesBounds(selection.nodes))
          }}
        >
          <Controls showInteractive={false}>
            <div
              className="map-controls-button"
              style={{
                padding: "4px 0",
                backgroundColor: showMinimap ? "#f4f4f4" : "#fff",
              }}
            >
              <MinimapIcon
                style={{ width: 19, height: 19 }}
                onClick={() => setShowMinimap(!showMinimap)}
              />
            </div>
            <div
              style={{
                position: "relative",
                width: 26,
                height: 26,
                cursor: "pointer",
                backgroundColor: isSelectionLocked ? "#f4f4f4" : "#fff",
              }}
              className="map-controls-button"
              onClick={() => {
                if (!isSelectionLocked) {
                  updateClusters()
                }

                setSelectionLocked(!isSelectionLocked)
              }}
            >
              {isSelectionLocked ? (
                <SelectionLockedIcon
                  style={{ position: "absolute", width: 26, height: 26 }}
                />
              ) : (
                <SelectionUnlockedIcon
                  style={{ position: "absolute", width: 26, height: 26 }}
                />
              )}
            </div>
          </Controls>
          <Background color="#aaa" gap={BACKGROUND_DOTS_GAP} />
          {showMinimap && <MiniMap pannable />}
        </ReactFlow>
      </AppContext.Provider>

      {customerSubscription?.is_active ? (
        <AutogenModal
          appElement={appContainerRef.current || undefined}
          isOpen={isAutogenerateDialogOpen}
          onRequestClose={() => setAutogenerateDialogOpen(false)}
          isLoadingAIResponse={isLoadingAIResponse}
          onConfirm={handleAutogenerateCanvas}
        />
      ) : (
        <UpgradeToProModal
          isOpen={isAutogenerateDialogOpen}
          onRequestClose={() => setAutogenerateDialogOpen(false)}
          customerTeam={customerTeam || ""}
        />
      )}

      <AutogenResultsModal
        appContainer={appContainerRef.current}
        isOpen={isAutogenerateResultsDialogOpen && !!autogenerateResult}
        setOpen={setAutogenerateResultsDialogOpen}
        autogenerateResult={autogenerateResult}
      />

      {customerSubscription?.is_active ? (
        <PublishApiModal
          open={!!(isApiPreviewVisible && apiPreview)}
          onClose={() => setIsApiPreviewVisible(false)}
          onPublishToFirebase={handlePublishToFirebase}
          onPublish={handlePublish}
          userType={userType}
          apiPreview={apiPreview}
          canvasId={canvas?.id!}
          enabledFeatures={enabledFeatures}
          validateApi={validateApi}
          testApi={testApi}
        />
      ) : (
        <UpgradeToProModal
          isOpen={isApiPreviewVisible}
          onRequestClose={() => setIsApiPreviewVisible(false)}
          customerTeam={customerTeam || ""}
        />
      )}

      <div className="api-controls">
        {multiplayerUsersInfo.map((user, i) => {
          return (
            <MultiplayerUserIndicator
              key={user.userId}
              firstName={user.firstName}
              lastName={user.lastName}
              userPicture={user.userPicture}
              userIndex={i}
            />
          )
        })}
        {!isApiPreviewVisible && (
          <button
            onClick={handleViewAndDeployAPI}
            className="view-api-button blue-button"
          >
            View and Publish API
          </button>
        )}
      </div>

      <div className="user-menu">
        <span
          className="user-menu-item"
          onClick={() =>
            setUserModalOpen((isUserModalOpen) => !isUserModalOpen)
          }
        >
          {session?.user?.email}
        </span>
        <span> / </span>
        <input
          value={canvasName}
          onChange={({ target }) => setCanvasName(target.value)}
          onBlur={({ target }) => {
            handleCanvasNameChange(target.value)
          }}
          className="canvas-name-input"
        />
        {!isEditingAllowed && (
          <span
            className="view-only-indicator"
            title="Canvas is in the view only mode for this user role"
          >
            View Only
          </span>
        )}
      </div>

      <UserModal
        appContainer={appContainerRef.current}
        isOpen={isUserModalOpen}
        setOpen={setUserModalOpen}
        setManageSubscriptionModalOpen={setManageSubscriptionModalOpen}
        setProfileModalOpen={setProfileModalOpen}
      />

      <MatrixLabelEditorModal
        appContainer={appContainerRef.current}
        isOpen={isMatrixLabelEditorOpen}
        setOpen={setMatrixLabelEditorOpen}
        matrixLabelInputRef={matrixLabelInputRef}
        matrixLabel={matrixLabel}
        setMatrixLabel={setMatrixLabel}
        handleConfirmMatrixLabel={handleConfirmMatrixLabel}
      />

      <ManageSubscriptionModal
        isOpen={isManageSubscriptionModalOpen}
        onRequestClose={() => setManageSubscriptionModalOpen(false)}
        usage={llmUsage?.usage_count}
        isSubscriptionActive={customerSubscription?.is_active}
        customerTeam={customerTeam || ""}
        subscriptionExpiryDate={customerSubscription?.end_date}
        subscriptionTier={subscriptionTier}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onRequestClose={() => setProfileModalOpen(false)}
        onUpdateUserPic={async (userPicture) => {
          if (session?.user.id) {
            await supabaseConnector.udateUserProfilePic(
              session.user.id,
              userPicture
            )
            setuserProfile((prev) => ({
              ...prev,
              userPicture,
            }))
          }
        }}
        onUpdateUserName={async (firstName, lastName) => {
          if (session?.user.id) {
            await supabaseConnector.udateUserProfileName(
              session.user.id,
              firstName,
              lastName
            )
            setuserProfile((prev) => ({
              ...prev,
              firstName,
              lastName,
            }))
          }
        }}
        userProfile={userProfile}
      />

      {multiplayerUserPointers.map((user, i) => {
        return (
          <MultiplayerUserMarker
            key={user.userId}
            firstName={user.firstName}
            lastName={user.lastName}
            mouseX={user.mouseX!}
            mouseY={user.mouseY!}
            userIndex={i}
          />
        )
      })}

      {backendError && (
        <div className="toaster-message">
          <span>{backendError}</span>
          <button
            onClick={() => {
              setBackendError(null)
              session && fetchData(session)
            }}
          >
            Refresh Data
          </button>
        </div>
      )}

      {!!testedApiSheetId ? (
        <ApiTestModal
          isOpen={!!testedApiSheetId}
          apiPreview={apiPreview || []}
          api={api || []}
          clusters={clusters}
          onRequestClose={() => {
            setTestedApiSheetId(null)
          }}
          sheetId={testedApiSheetId!}
          nodes={nodes}
        />
      ) : null}
    </ErrorBoundary>
  )
}

export default App
